import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Lightbulb, Heart, MessageCircle, Star, Award, Globe, ArrowRight,
  BadgeCheck, GraduationCap, Languages, BookOpen, Mic, Mail, Phone,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Lightbox from '../components/Lightbox';
import { useLang } from '../useLang';
import {
  images, impactIcons, topicMeta, credentialIcons, trustIcons, testimonialVideo,
  testimonialVideo2, walkInVideo, galleryImages, galleryPositions, bookPresaleUrl,
} from '../siteConfig';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';

const iconMap = {
  Users, Lightbulb, Heart, MessageCircle, Star, Award, Globe,
  BadgeCheck, GraduationCap, Languages, BookOpen, Mic,
};

const SalesPage = () => {
  const navigate = useNavigate();
  const { lang, t } = useLang();

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  }, []);

  const impactColors = ['var(--coral)', 'var(--gold)', 'var(--coral)', 'var(--navy)', 'var(--gold)'];

  const testimonialItems = t.testimonials.items;
  const [tIndex, setTIndex] = useState(0);
  useEffect(() => { setTIndex(0); }, [lang]);
  const tPrev = () => setTIndex((i) => (i - 1 + testimonialItems.length) % testimonialItems.length);
  const tNext = () => setTIndex((i) => (i + 1) % testimonialItems.length);

  const [lbIndex, setLbIndex] = useState(null);
  const lightboxItems = [
    ...t.credentials.experiences.map((exp, i) => ({ src: i === 0 ? images.expMontreal : images.expBenin, caption: exp.heading })),
    ...galleryImages.map((src, i) => ({ src, caption: t.credentials.galleryCaptions?.[i] || '' })),
  ];
  const expCount = t.credentials.experiences.length;

  return (
    <div className="bg-cream min-h-screen">
      <Lightbox items={lightboxItems} index={lbIndex} onChange={setLbIndex} labels={t.lightbox || {}} />
      <Header />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-14">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="fade-up">
            <p className="font-head font-bold tracking-[0.15em] text-sm mb-5" style={{ color: 'var(--coral)' }}>
              {t.hero.badge}
            </p>
            <h1 className="font-head font-extrabold leading-[1.05] text-[2.6rem] md:text-6xl" style={{ color: 'var(--navy)' }}>
              {t.hero.titleLines.map((line, i) => (
                <React.Fragment key={i}>{line}{i < t.hero.titleLines.length - 1 && <br />}</React.Fragment>
              ))}
            </h1>
            <p className="font-body text-lg mt-6 max-w-xl leading-relaxed" style={{ color: 'var(--ink)' }}>
              {t.hero.desc}
            </p>
            <button onClick={() => navigate(`/${lang}/book`)}
              className="btn-coral font-head font-bold tracking-wide text-base md:text-lg px-8 py-4 rounded-lg mt-8 flex items-center gap-2">
              {t.hero.cta} <ArrowRight className="w-5 h-5" />
            </button>
            <p className="flex items-center gap-2 mt-5 font-body" style={{ color: 'var(--ink)' }}>
              <Heart className="w-4 h-4" style={{ color: 'var(--coral)' }} fill="var(--coral)" />
              {t.hero.forSchools}
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4">
              <a href="mailto:koulrachelle09@gmail.com" className="flex items-center gap-2 font-body font-semibold transition-colors hover:opacity-80" style={{ color: 'var(--coral)' }}>
                <Mail className="w-4 h-4" /> koulrachelle09@gmail.com
              </a>
              <a href="tel:+15875854685" className="flex items-center gap-2 font-body font-semibold transition-colors hover:opacity-80" style={{ color: 'var(--coral)' }}>
                <Phone className="w-4 h-4" /> 587-585-4685
              </a>
            </div>
          </div>

          <div className="relative fade-up">
            <div className="overflow-hidden rounded-[28px] shadow-2xl">
              <img src={images.hero} alt="Rachelle Koulibaly" className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 md:-left-8 w-32 h-32 md:w-40 md:h-40 rounded-full flex flex-col items-center justify-center text-center shadow-xl" style={{ background: 'var(--gold)' }}>
              <span className="font-head font-bold text-navy text-[13px] md:text-sm leading-tight px-3">{t.hero.badgeCircle}</span>
              <Heart className="w-4 h-4 mt-1" style={{ color: 'var(--coral)' }} fill="var(--coral)" />
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14">
        <h2 className="font-head font-extrabold text-3xl md:text-4xl text-center mb-12" style={{ color: 'var(--navy)' }}>
          {t.impact.heading}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {t.impact.features.map((title, i) => {
            const Icon = iconMap[impactIcons[i]];
            return (
              <div key={i} className="flex flex-col items-center text-center px-2">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(0,0,0,.03)' }}>
                  <Icon className="w-8 h-8" style={{ color: impactColors[i] }} strokeWidth={1.6} />
                </div>
                <p className="font-body font-semibold text-sm leading-snug" style={{ color: 'var(--ink)' }}>{title}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TOPICS */}
      <section id="topics" className="max-w-7xl mx-auto px-5 md:px-8 py-14 scroll-mt-24">
        <h2 className="font-head font-extrabold text-3xl md:text-4xl text-center mb-12" style={{ color: 'var(--navy)' }}>
          {t.topics.heading}
        </h2>
        <div className="flex flex-wrap justify-center gap-6">
          {t.topics.items.map((topic, i) => {
            const meta = topicMeta[i] || topicMeta[0];
            const Icon = meta.icon;
            return (
              <div key={i} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] bg-white rounded-2xl p-7 text-center hover-lift border border-black/[0.04]">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: 'rgba(0,0,0,.03)' }}>
                  <Icon className="w-7 h-7" style={{ color: meta.color }} strokeWidth={1.7} />
                </div>
                <h3 className="font-head font-bold text-lg mb-3" style={{ color: meta.titleColor }}>{topic.title}</h3>
                <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--ink)' }}>{topic.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-cream-deep scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="font-head font-bold tracking-[0.15em] text-sm mb-4" style={{ color: 'var(--coral)' }}>{t.about.label}</p>
            <h2 className="font-head font-extrabold text-3xl md:text-4xl mb-5" style={{ color: 'var(--navy)' }}>{t.about.heading}</h2>
            <p className="font-body text-lg leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>{t.about.p1}</p>
            <p className="font-body text-lg leading-relaxed" style={{ color: 'var(--ink)' }}>{t.about.p2}</p>
            <p className="font-body text-lg leading-relaxed mt-4" style={{ color: 'var(--ink)' }}>
              {t.about.founderPre}
              <span className="font-head font-bold" style={{ color: 'var(--coral)' }}>{t.about.founderName}</span>
              {t.about.founderPost}
            </p>
          </div>
          <div className="order-1 lg:order-2 overflow-hidden rounded-[28px] shadow-xl">
            <img src={images.testimonial} alt="Rachelle Koulibaly" className="w-full h-[440px] lg:h-[500px] object-cover object-[center_15%]" data-testid="about-portrait" />
          </div>
        </div>

        {/* STORY BEHIND THE TALK */}
        <div className="max-w-4xl mx-auto px-5 md:px-8 pb-16">
          <h3 className="font-head font-extrabold text-2xl md:text-3xl mb-6 text-center" style={{ color: 'var(--navy)' }}>
            {t.about.storyHeading}
          </h3>

          <blockquote className="relative bg-white rounded-2xl p-7 md:p-9 shadow-sm border-l-4 mb-8" style={{ borderColor: 'var(--coral)' }}>
            <span className="font-script text-6xl absolute -top-3 left-4 leading-none" style={{ color: 'var(--gold)' }}>&ldquo;</span>
            <p className="font-body italic text-lg md:text-xl leading-relaxed pt-4" style={{ color: 'var(--navy)' }}>
              {t.about.quote}
            </p>
          </blockquote>

          <div className="font-body text-lg leading-relaxed space-y-4" style={{ color: 'var(--ink)' }}>
            <p>{t.about.storyIntro}</p>
            <p>{t.about.storyBackground}</p>
            <p>
              {t.about.storyMovePre}
              <span className="italic" style={{ color: 'var(--navy)' }}>{t.about.storyQuestion}</span>
            </p>
            <p>
              {t.about.storyAnswerPre}
              <span className="font-head font-bold" style={{ color: 'var(--coral)' }}>{t.about.storyBlueprint}</span>
              {t.about.storyAnswerPost}
            </p>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16">
        <h2 className="font-head font-extrabold text-3xl md:text-4xl text-center mb-3" style={{ color: 'var(--navy)' }}>
          {t.credentials.heading}
        </h2>
        <p className="font-body text-lg text-center mb-12 max-w-3xl mx-auto" style={{ color: 'var(--ink)' }}>
          {t.credentials.sub}
        </p>

        {/* FEATURED WALK-IN VIDEO */}
        <div className="max-w-xl mx-auto mb-12" data-testid="walk-in-video">
          <div className="rounded-[26px] p-2 shadow-xl" style={{ background: 'linear-gradient(135deg, var(--primary-mid), var(--secondary-blue))' }}>
            <div className="rounded-[18px] overflow-hidden bg-black">
              <video
                src={walkInVideo}
                controls
                playsInline
                preload="metadata"
                className="w-full h-auto block"
              />
            </div>
          </div>
          {t.credentials.videoCaption && (
            <p className="font-head font-semibold text-sm md:text-[15px] text-center mt-4" style={{ color: 'var(--navy)' }} data-testid="walk-in-video-caption">
              {t.credentials.videoCaption}
            </p>
          )}
        </div>

        {/* SPEAKING EXPERIENCES - split down the middle */}
        <div className="grid md:grid-cols-2 gap-px rounded-2xl overflow-hidden border border-black/[0.06] bg-black/[0.06] mb-10" data-testid="speaking-experiences">
          {t.credentials.experiences.map((exp, i) => (
            <div key={i} className="bg-white p-7 md:p-8" data-testid={`experience-card-${i}`}>
              <button
                type="button"
                onClick={() => setLbIndex(i)}
                aria-label={exp.heading}
                className="block w-full sm:float-left sm:w-64 h-56 rounded-xl overflow-hidden mb-3 sm:mr-5 shadow-sm cursor-zoom-in group"
                data-testid={`experience-image-${i}`}
              >
                <img
                  src={i === 0 ? images.expMontreal : images.expBenin}
                  alt={exp.heading}
                  className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
                />
              </button>
              <h3 className="font-head font-extrabold text-lg md:text-xl mb-1.5" style={{ color: 'var(--navy)' }}>
                {exp.heading}
              </h3>
              {exp.subheading && (
                <p className="font-head font-semibold text-[15px] mb-2.5" style={{ color: 'var(--coral)' }}>
                  {exp.subheading}
                </p>
              )}
              {exp.paras.map((p, j) => (
                <p key={j} className="font-body text-[14.5px] leading-relaxed mb-2.5" style={{ color: 'var(--ink)' }}>
                  {p}
                </p>
              ))}
              <div className="clear-both" />
            </div>
          ))}
        </div>

        {/* GALLERY */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 mb-10" data-testid="experience-gallery">
          {galleryImages.map((src, i) => (
            <figure
              key={i}
              className={`${i === 0 || (i === galleryImages.length - 1 && galleryImages.length % 2 === 0) ? 'col-span-2' : ''} md:col-span-2`}
            >
              <button
                type="button"
                onClick={() => setLbIndex(expCount + i)}
                aria-label={t.credentials.galleryCaptions?.[i]}
                className="block w-full rounded-xl overflow-hidden shadow-sm bg-black/[0.03] cursor-zoom-in"
              >
                <img
                  src={src}
                  alt={t.credentials.galleryCaptions?.[i] || `Rachelle Koulibaly speaking engagement ${i + 1}`}
                  className="w-full h-64 md:h-72 object-cover hover:scale-[1.03] transition-transform duration-500"
                  style={{ objectPosition: galleryPositions[i] }}
                  data-testid={`gallery-image-${i}`}
                />
              </button>
              {t.credentials.galleryCaptions?.[i] && (
                <figcaption className="font-head font-semibold text-[13px] md:text-sm text-center mt-2.5 leading-snug" style={{ color: 'var(--navy)' }} data-testid={`gallery-caption-${i}`}>
                  {t.credentials.galleryCaptions[i]}
                </figcaption>
              )}
            </figure>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {t.credentials.items.map((c, i) => {
            const Icon = iconMap[credentialIcons[i]];
            return (
              <div key={i} className="bg-white rounded-2xl p-7 flex items-start gap-5 hover-lift border border-black/[0.04]">
                <div className="w-14 h-14 shrink-0 rounded-xl flex items-center justify-center" style={{ background: 'rgba(97,93,210,.10)' }}>
                  <Icon className="w-7 h-7" style={{ color: 'var(--navy)' }} strokeWidth={1.7} />
                </div>
                <div>
                  <h3 className="font-head font-bold text-lg mb-1.5" style={{ color: 'var(--navy)' }}>{c.title}</h3>
                  <p className="font-body text-[15px] leading-relaxed" style={{ color: 'var(--ink)' }}>{c.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* BILINGUAL - standout */}
        <div className="relative mt-6 rounded-2xl p-8 md:p-9 overflow-hidden shadow-lg" style={{ background: 'var(--navy)' }}>
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full -mr-16 -mt-16" style={{ background: 'rgba(72,182,247,.20)' }} />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center" style={{ background: 'var(--gold)' }}>
              <Languages className="w-8 h-8" style={{ color: 'var(--navy)' }} strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-1.5">
                <h3 className="font-head font-extrabold text-2xl text-white">{t.credentials.bilingual.title}</h3>
                <span className="font-head font-bold text-[11px] tracking-[0.15em] uppercase px-3 py-1 rounded-full" style={{ background: 'var(--gold)', color: 'var(--navy)' }}>
                  {t.credentials.bilingual.badge}
                </span>
              </div>
              <p className="font-body text-lg leading-relaxed text-white/90">
                {t.credentials.bilingual.pre}
                <span className="font-semibold" style={{ color: 'var(--gold)' }}>{t.credentials.bilingual.highlight}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CAREER HIGHLIGHTS */}
      <section className="bg-cream-deep">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16">
          <h2 className="font-head font-extrabold text-3xl md:text-4xl text-center mb-3" style={{ color: 'var(--navy)' }}>
            {t.careerHighlights.heading}
          </h2>
          <p className="font-body text-lg text-center mb-14 max-w-2xl mx-auto" style={{ color: 'var(--ink)' }}>
            {t.careerHighlights.sub}
          </p>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Book cover */}
            <div className="flex flex-col items-center order-1">
              <div className="relative">
                <img
                  src={images.book}
                  alt="The Mother Behind This Child — book by Rachelle Koulibaly"
                  className="w-full max-w-[380px] mix-blend-multiply drop-shadow-2xl"
                />
              </div>
              <a
                href={bookPresaleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-head font-bold tracking-wide text-base px-8 py-3.5 rounded-lg inline-flex items-center gap-2 mt-7 shadow-md transition-[transform,filter] duration-200 hover:-translate-y-0.5 hover:brightness-95"
                style={{ background: 'var(--gold)', color: 'var(--navy)' }}
                data-testid="book-preorder-btn"
              >
                {t.careerHighlights.preorderCta} <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            {/* Timeline */}
            <div className="order-2">
              {t.careerHighlights.items.map((h, i) => {
                const Icon = iconMap[h.icon] || Award;
                const colors = ['var(--coral)', 'var(--teal)', 'var(--gold)', 'var(--navy)'];
                const dotColor = h.featured ? 'var(--gold)' : colors[i % colors.length];
                const isLast = i === t.careerHighlights.items.length - 1;
                return (
                  <div key={i} className="flex gap-5">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center shadow-sm" style={{ background: dotColor }}>
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                      </div>
                      {!isLast && <div className="w-0.5 flex-1 my-2" style={{ background: 'rgba(97,93,210,.25)' }} />}
                    </div>
                    <div className={isLast ? 'pb-0' : 'pb-9'}>
                      <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
                        <h3 className="font-head font-bold text-lg leading-tight" style={{ color: 'var(--navy)' }}>{h.title}</h3>
                        {h.featured && (
                          <span className="font-head font-extrabold text-[10px] tracking-[0.15em] uppercase px-2.5 py-0.5 rounded-full" style={{ background: 'var(--gold)', color: 'var(--navy)' }}>
                            Book
                          </span>
                        )}
                      </div>
                      <p className="font-body text-[15px] leading-relaxed" style={{ color: 'var(--ink)' }}>{h.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="max-w-7xl mx-auto px-5 md:px-8 py-16 scroll-mt-24">
        <h2 className="font-head font-extrabold text-3xl md:text-4xl text-center mb-12" style={{ color: 'var(--navy)' }}>
          {t.testimonials.heading}
        </h2>
        <div className="max-w-4xl mx-auto mb-14 grid md:grid-cols-2 gap-6" data-testid="testimonial-videos">
          {[testimonialVideo, testimonialVideo2].map((src, i) => (
            <div className="relative" key={i}>
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 bg-white shadow-md rounded-full px-4 py-1.5 border border-black/[0.05]">
                <Heart className="w-3.5 h-3.5" style={{ color: 'var(--coral)' }} fill="var(--coral)" />
                <span className="font-head font-bold text-[11px] tracking-[0.12em] uppercase" style={{ color: 'var(--navy)' }}>Featured Testimonial</span>
              </div>
              <div className="rounded-[26px] p-2 shadow-xl" style={{ background: 'linear-gradient(135deg, var(--primary-mid), var(--secondary-blue))' }}>
                <div className="rounded-[18px] overflow-hidden bg-black">
                  <video
                    src={src}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-auto block max-h-[520px]"
                    data-testid={`testimonial-video-${i}`}
                  />
                </div>
              </div>
              {t.testimonials.videos?.[i] && (
                <div className="text-center mt-5 px-2" data-testid={`testimonial-video-copy-${i}`}>
                  <h3 className="font-head font-bold text-lg leading-snug" style={{ color: 'var(--navy)' }}>{t.testimonials.videos[i].title}</h3>
                  <p className="font-body text-[15px] leading-relaxed mt-1.5" style={{ color: 'var(--ink)' }}>{t.testimonials.videos[i].desc}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="relative max-w-5xl mx-auto px-2 sm:px-10">
          <div className="grid md:grid-cols-2 gap-6">
            {[0, 1].map((offset) => {
              const item = testimonialItems[(tIndex + offset) % testimonialItems.length];
              return (
                <div key={offset} className={`bg-white rounded-2xl border border-black/[0.05] shadow-lg p-8 md:p-10 relative min-h-[280px] flex flex-col justify-center ${offset === 1 ? 'hidden md:flex' : ''}`}>
                  <span className="font-script text-6xl absolute top-3 left-6 leading-none" style={{ color: 'var(--coral)' }}>&ldquo;</span>
                  <p key={`${tIndex}-${offset}`} className="font-body text-base md:text-lg leading-relaxed pt-6 fade-up" style={{ color: 'var(--ink)' }}>
                    {item.quote}
                  </p>
                  <p className="font-head font-bold mt-5" style={{ color: 'var(--navy)' }}>&mdash; {item.author}</p>
                </div>
              );
            })}
          </div>

          {/* Arrows */}
          <button
            onClick={tPrev}
            aria-label="Previous testimonial"
            className="absolute left-0 sm:-left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-md border border-black/[0.06] flex items-center justify-center transition-transform hover:scale-110 z-10"
          >
            <ChevronLeft className="w-6 h-6" style={{ color: 'var(--coral)' }} />
          </button>
          <button
            onClick={tNext}
            aria-label="Next testimonial"
            className="absolute right-0 sm:-right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-md border border-black/[0.06] flex items-center justify-center transition-transform hover:scale-110 z-10"
          >
            <ChevronRight className="w-6 h-6" style={{ color: 'var(--coral)' }} />
          </button>

          {/* Dots */}
          <div className="flex justify-center flex-wrap gap-2 mt-7">
            {testimonialItems.map((_, i) => (
              <button
                key={i}
                onClick={() => setTIndex(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                className="h-2.5 rounded-full transition-all"
                style={{
                  width: i === tIndex ? '26px' : '10px',
                  background: i === tIndex ? 'var(--coral)' : 'rgba(97,93,210,.30)',
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="bg-cream-deep scroll-mt-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16">
          <h2 className="font-head font-extrabold text-3xl md:text-4xl text-center mb-10" style={{ color: 'var(--navy)' }}>
            {t.faqs.heading}
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {t.faqs.items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-black/[0.05] px-5">
                <AccordionTrigger className="font-head font-bold text-left hover:no-underline" style={{ color: 'var(--navy)' }}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-base leading-relaxed" style={{ color: 'var(--ink)' }}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-16 text-center flex flex-col items-center">
        <img src={images.cta} alt="Rachelle Koulibaly" className="w-24 h-24 rounded-full object-cover shadow-lg ring-4 ring-white mb-6" />
        <h2 className="font-head font-extrabold text-3xl md:text-4xl mb-4" style={{ color: 'var(--navy)' }}>{t.ctaStrip.heading}</h2>
        <p className="font-body text-lg mb-8" style={{ color: 'var(--ink)' }}>{t.ctaStrip.sub}</p>
        <button onClick={() => navigate(`/${lang}/book`)} className="btn-coral font-head font-bold tracking-wide text-lg px-9 py-4 rounded-lg inline-flex items-center gap-2">
          {t.ctaStrip.button} <ArrowRight className="w-5 h-5" />
        </button>
      </section>

      {/* TRUST BADGES BAND */}
      <section className="bg-footer-blue">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {t.trust.map((label, i) => {
            const Icon = iconMap[trustIcons[i]];
            return (
              <div key={i} className="flex items-center gap-3 justify-center text-center">
                <Icon className="w-8 h-8 shrink-0 text-white/90" strokeWidth={1.4} />
                <span className="font-head font-semibold text-white text-sm md:text-base text-left">{label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <Footer tagline={t.footer.tagline} />
    </div>
  );
};

export default SalesPage;
